import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FiEdit3, FiEye, FiMessageSquare, FiRefreshCw, FiSearch, FiTrendingUp, FiX } from "react-icons/fi";
import axios from "../api/axiosInstance";

const DEFAULT_FILTERS = { search: "", topic_id: "all", status: "all", priority: "all" };
const DEFAULT_FEEDBACK_FORM = {
  feedback_type: "clarification",
  feedback_text: "",
  is_global_constraint: false,
  constraint_summary: "",
};

function toQueryString(params) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "" || value === "all") return;
    search.append(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : "";
}

function formatDate(value) {
  if (!value) return "No date";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsed);
}

function formatScore(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "Pending";
  return `${Math.round(numeric)}/100`;
}

function truncateText(value, maxLength = 170) {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength - 3)}...` : normalized;
}

function getStatusLabel(status) {
  switch (String(status || "").toLowerCase()) {
    case "in_progress":
      return "In progress";
    case "blocked":
      return "Blocked";
    case "solved":
      return "Solved";
    case "dismissed":
      return "Dismissed";
    default:
      return "Open";
  }
}

function getStatusTone(status) {
  switch (String(status || "").toLowerCase()) {
    case "in_progress":
      return "info";
    case "blocked":
      return "danger";
    case "solved":
      return "success";
    case "dismissed":
      return "muted";
    default:
      return "warning";
  }
}

function getPriorityLabel(priority) {
  switch (String(priority || "").toLowerCase()) {
    case "high":
      return "High";
    case "medium":
      return "Medium";
    case "low":
      return "Low";
    default:
      return "Review";
  }
}

function getPriorityTone(priority) {
  switch (String(priority || "").toLowerCase()) {
    case "high":
      return "danger";
    case "medium":
      return "warning";
    case "low":
      return "success";
    default:
      return "neutral";
  }
}

function buildToneClass(prefix, tone) {
  return `${prefix} ${prefix}--${tone}`;
}

function Modal({ open, title, subtitle, onClose, children }) {
  if (!open) return null;
  return (
    <div className="tap-modalBackdrop" onClick={onClose}>
      <div className="tap-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
        <div className="tap-modalHeader">
          <div>
            <h2 className="tap-modalTitle">{title}</h2>
            {subtitle ? <p className="tap-modalSubtitle">{subtitle}</p> : null}
          </div>
          <button type="button" className="tap-iconBtn" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>
        <div className="tap-modalBody">{children}</div>
      </div>
    </div>
  );
}

function TrendsActionsPage() {
  const [searchParams] = useSearchParams();
  const businessUnitId = searchParams.get("business_unit_id") || "";
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [businessUnit, setBusinessUnit] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    topics: [],
    owners: [],
    status_options: [],
    priority_options: [],
  });
  const [actionPlans, setActionPlans] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total_items: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingPlans, setRefreshingPlans] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedPlanDetail, setSelectedPlanDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [planForm, setPlanForm] = useState({
    status: "open",
    priority: "medium",
    owner_user_id: "",
    human_feedback: "",
    final_recommendation: "",
  });
  const [feedbackForm, setFeedbackForm] = useState(DEFAULT_FEEDBACK_FORM);
  const [actionLoading, setActionLoading] = useState("");

  const fetchActionPlans = async ({ page = 1, silent = false, nextFilters } = {}) => {
    const effectiveFilters = nextFilters || filters;
    silent ? setRefreshing(true) : setLoading(true);
    setError("");

    try {
      const query = toQueryString({
        business_unit_id: businessUnitId || undefined,
        page,
        search: effectiveFilters.search,
        topic_id: effectiveFilters.topic_id,
        status: effectiveFilters.status,
        priority: effectiveFilters.priority,
      });
      const response = await axios.get(`/api/reviews-intelligence/action-plans${query}`);
      setBusinessUnit(response.data?.business_unit || null);
      setSummary(response.data?.summary || null);
      setFilterOptions(response.data?.filters || { topics: [], owners: [], status_options: [], priority_options: [] });
      setActionPlans(Array.isArray(response.data?.action_plans) ? response.data.action_plans : []);
      setPagination(response.data?.pagination || { page: 1, page_size: 20, total_items: 0, total_pages: 1 });
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "Error loading action plans.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setFilters(DEFAULT_FILTERS);
    setSelectedPlanId(null);
    setSelectedPlanDetail(null);
    setDetailError("");
    setFeedback("");
    fetchActionPlans({ page: 1, nextFilters: DEFAULT_FILTERS });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId]);

  const fetchActionPlanDetail = async (actionPlanId) => {
    setDetailLoading(true);
    setDetailError("");

    try {
      const query = toQueryString({ business_unit_id: businessUnitId || undefined });
      const response = await axios.get(`/api/reviews-intelligence/action-plans/${actionPlanId}${query}`);
      const detail = response.data?.action_plan || null;
      setSelectedPlanDetail({ ...response.data, action_plan: detail });
      setPlanForm({
        status: detail?.status || "open",
        priority: detail?.priority || "medium",
        owner_user_id: detail?.owner_user?.id ? String(detail.owner_user.id) : "",
        human_feedback: detail?.human_feedback || "",
        final_recommendation: detail?.final_recommendation || "",
      });
      setFeedbackForm(DEFAULT_FEEDBACK_FORM);
    } catch (requestError) {
      setDetailError(requestError?.response?.data?.error || "Error loading action plan detail.");
    } finally {
      setDetailLoading(false);
    }
  };

  const openPlanDetail = async (actionPlanId) => {
    setSelectedPlanId(actionPlanId);
    setSelectedPlanDetail(null);
    await fetchActionPlanDetail(actionPlanId);
  };

  const closePlanDetail = () => {
    setSelectedPlanId(null);
    setSelectedPlanDetail(null);
    setDetailError("");
    setFeedbackForm(DEFAULT_FEEDBACK_FORM);
  };

  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const applyFilters = async () => {
    await fetchActionPlans({ page: 1, nextFilters: filters });
  };

  const handlePageChange = async (nextPage) => {
    if (nextPage < 1 || nextPage > (pagination.total_pages || 1) || nextPage === pagination.page) return;
    await fetchActionPlans({ page: nextPage, silent: true });
  };

  const refreshActionPlanSuggestions = async () => {
    setRefreshingPlans(true);
    setFeedback("");
    setError("");

    try {
      const response = await axios.post("/api/reviews-intelligence/action-plans/refresh", {
        business_unit_id: businessUnitId,
      });
      setFeedback(response.data?.message || "Action plans refreshed from the latest review patterns.");
      await fetchActionPlans({ page: 1, silent: true });
      if (selectedPlanId) await fetchActionPlanDetail(selectedPlanId);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "Error refreshing action plans.");
    } finally {
      setRefreshingPlans(false);
    }
  };

  const saveActionPlan = async () => {
    if (!selectedPlanId) return;
    setActionLoading("save_plan");
    setDetailError("");
    setFeedback("");

    try {
      await axios.patch(`/api/reviews-intelligence/action-plans/${selectedPlanId}`, {
        business_unit_id: businessUnitId,
        status: planForm.status,
        priority: planForm.priority,
        owner_user_id: planForm.owner_user_id || null,
        human_feedback: planForm.human_feedback,
        final_recommendation: planForm.final_recommendation,
      });
      setFeedback("Action plan updated.");
      await fetchActionPlans({ page: pagination.page || 1, silent: true });
      await fetchActionPlanDetail(selectedPlanId);
    } catch (requestError) {
      setDetailError(requestError?.response?.data?.error || "Error updating the action plan.");
    } finally {
      setActionLoading("");
    }
  };

  const saveFeedbackEntry = async () => {
    if (!selectedPlanId) return;
    setActionLoading("save_feedback");
    setDetailError("");
    setFeedback("");

    try {
      const response = await axios.post(`/api/reviews-intelligence/action-plans/${selectedPlanId}/feedback`, {
        business_unit_id: businessUnitId,
        feedback_type: feedbackForm.feedback_type,
        feedback_text: feedbackForm.feedback_text,
        is_global_constraint: feedbackForm.is_global_constraint,
        constraint_summary: feedbackForm.constraint_summary,
      });
      setFeedback(response.data?.message || "Feedback saved.");
      setFeedbackForm(DEFAULT_FEEDBACK_FORM);
      await fetchActionPlans({ page: pagination.page || 1, silent: true });
      await fetchActionPlanDetail(selectedPlanId);
    } catch (requestError) {
      setDetailError(requestError?.response?.data?.error || "Error saving the action plan feedback.");
    } finally {
      setActionLoading("");
    }
  };

  const currentRangeText = useMemo(() => {
    const total = pagination.total_items || 0;
    if (!total) return "No action plans yet.";
    const start = ((pagination.page || 1) - 1) * (pagination.page_size || 20) + 1;
    const end = Math.min(start + (pagination.page_size || 20) - 1, total);
    return `${start}-${end} of ${total} action plans`;
  }, [pagination]);

  const uiCss = `
    .reservations-container.tap-pageShell{padding:10px}
    .reservations-container .tap-shell{background:linear-gradient(180deg,rgba(25,27,53,.94),rgba(11,12,25,.97));border:1px solid rgba(255,255,255,.08);border-radius:22px;box-shadow:0 24px 64px rgba(3,7,18,.32);overflow:hidden}
    .reservations-container .tap-header{display:flex;justify-content:space-between;align-items:flex-end;gap:18px;padding:18px 18px 14px;border-bottom:1px solid rgba(255,255,255,.08);flex-wrap:wrap}
    .reservations-container .tap-title{margin:0;font-size:1.38rem;font-weight:800;letter-spacing:-.02em;color:rgba(244,248,255,.98)}
    .reservations-container .tap-subtitle{margin:6px 0 0;font-size:.82rem;line-height:1.55;color:rgba(214,226,247,.68);max-width:840px}
    .reservations-container .tap-hotel{margin-top:8px;font-size:.76rem;color:rgba(214,226,247,.62)}
    .reservations-container .tap-actions{display:flex;gap:8px;flex-wrap:wrap}
    .reservations-container .tap-toolbarBtn{border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.04);color:rgba(244,248,255,.92);border-radius:10px;padding:8px 12px;font-size:.78rem;font-weight:700;display:inline-flex;align-items:center;gap:7px}
    .reservations-container .tap-toolbarBtn:disabled{opacity:.45;cursor:not-allowed}
    .reservations-container .tap-notice{margin:14px 18px 0;padding:12px 14px;border-radius:14px;font-size:.78rem;line-height:1.55;border:1px solid rgba(255,255,255,.08)}
    .reservations-container .tap-notice--success{background:rgba(25,164,122,.16);color:rgba(176,246,220,.98);border-color:rgba(25,164,122,.32)}
    .reservations-container .tap-notice--error{background:rgba(177,48,69,.22);color:rgba(255,213,213,.98);border-color:rgba(255,125,147,.32)}
    .reservations-container .tap-body{display:flex;flex-direction:column;gap:14px;padding:14px 18px 18px}
    .reservations-container .tap-summary{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:10px}
    .reservations-container .tap-summaryCard,.tap-card,.tap-listItem{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:16px}
    .reservations-container .tap-summaryCard{padding:14px 14px 13px}
    .reservations-container .tap-summaryLabel,.tap-cardLabel{font-size:.66rem;letter-spacing:.08em;text-transform:uppercase;color:rgba(214,226,247,.58);font-weight:800}
    .reservations-container .tap-summaryValue,.tap-cardValue{margin-top:8px;font-size:1.06rem;font-weight:850;color:rgba(245,248,255,.98);overflow-wrap:anywhere;word-break:break-word}
    .reservations-container .tap-summaryMeta,.tap-cardMeta,.tap-cellMeta,.tap-help{margin-top:5px;font-size:.74rem;line-height:1.5;color:rgba(214,226,247,.66);overflow-wrap:anywhere;word-break:break-word}
    .reservations-container .tap-filters{display:flex;gap:10px;align-items:center;flex-wrap:wrap}
    .reservations-container .tap-search{position:relative;min-width:260px;flex:1}
    .reservations-container .tap-search svg{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:rgba(214,226,247,.45)}
    .reservations-container .tap-input,.reservations-container .tap-select,.tap-textarea{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.14);color:rgba(244,248,255,.94)}
    .reservations-container .tap-input,.reservations-container .tap-select{border-radius:10px;height:38px;padding:0 12px;font-size:.78rem}
    .reservations-container .tap-input{width:100%;padding-left:36px}
    .reservations-container .tap-tableWrap{border:1px solid rgba(255,255,255,.08);border-radius:18px;overflow:auto;background:rgba(7,10,20,.36)}
    .reservations-container .tap-table{width:100%;min-width:1140px;border-collapse:separate;border-spacing:0;table-layout:fixed}
    .reservations-container .tap-table th,.reservations-container .tap-table td{padding:12px 16px;text-align:left;white-space:normal;overflow-wrap:anywhere;word-break:break-word}
    .reservations-container .tap-table th{font-size:.68rem;letter-spacing:.08em;text-transform:uppercase;color:rgba(214,226,247,.52);border-bottom:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03)}
    .reservations-container .tap-table td{font-size:.8rem;vertical-align:top;color:rgba(230,236,247,.88);border-bottom:1px solid rgba(255,255,255,.06)}
    .reservations-container .tap-table tr:hover td{background:rgba(255,255,255,.02)}
    .reservations-container .tap-colTopic{width:180px}.reservations-container .tap-colScores{width:160px}.reservations-container .tap-colPriority{width:110px}.reservations-container .tap-colStatus{width:118px}.reservations-container .tap-colOwner{width:150px}.reservations-container .tap-colOpen{width:110px}
    .reservations-container .tap-cellTitle,.tap-sectionTitle{font-size:.84rem;font-weight:700;color:rgba(245,248,255,.97)}
    .reservations-container .tap-cellText,.tap-sectionText{line-height:1.6;white-space:pre-wrap;overflow-wrap:anywhere;word-break:break-word}
    .reservations-container .tap-chip{display:inline-flex;align-items:center;justify-content:center;border-radius:999px;padding:5px 10px;font-size:.68rem;font-weight:800;white-space:nowrap}
    .reservations-container .tap-chip--neutral{background:rgba(255,255,255,.08);color:rgba(212,224,244,.95)}.reservations-container .tap-chip--success{background:rgba(25,164,122,.25);color:rgba(176,246,220,.98)}.reservations-container .tap-chip--warning{background:rgba(255,193,95,.14);color:rgba(255,230,185,.98)}.reservations-container .tap-chip--danger{background:rgba(177,48,69,.24);color:rgba(255,204,204,.98)}.reservations-container .tap-chip--info{background:rgba(52,152,219,.18);color:rgba(198,230,255,.98)}.reservations-container .tap-chip--muted{background:rgba(255,255,255,.05);color:rgba(214,226,247,.65)}
    .reservations-container .tap-openBtn{padding:6px 10px;font-size:.76rem;display:inline-flex;align-items:center;gap:6px}
    .reservations-container .tap-empty{padding:30px;text-align:center;font-size:.82rem;line-height:1.7;color:rgba(214,226,247,.72)}
    .reservations-container .tap-pagination{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;padding:0 4px}
    .reservations-container .tap-pageText{font-size:.72rem;color:rgba(230,230,247,.62)}
    .reservations-container .tap-pageControls{display:flex;gap:8px;align-items:center}
    .reservations-container .tap-pageBtn{border:1px solid rgba(255,255,255,.22);color:rgba(230,230,247,.78);background:transparent;padding:4px 8px;border-radius:6px;font-size:.72rem;font-weight:600;line-height:1.2}.reservations-container .tap-pageBtn:disabled{cursor:not-allowed;opacity:.35}
    .tap-modalBackdrop{position:fixed;inset:0;background:rgba(4,8,18,.68);backdrop-filter:blur(8px);display:flex;justify-content:center;align-items:flex-start;padding:34px 16px;z-index:1200;overflow-y:auto}
    .tap-modal{width:min(1240px,100%);border-radius:24px;border:1px solid transparent;background:linear-gradient(180deg,rgba(24,24,48,.98),rgba(10,10,22,.98)) padding-box,linear-gradient(135deg,rgba(48,216,255,.45),rgba(168,85,247,.50),rgba(255,77,79,.28)) border-box;box-shadow:0 32px 80px rgba(10,17,20,.46);overflow:hidden;color:rgba(229,237,250,.92)}
    .tap-modalHeader{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;padding:22px 24px;border-bottom:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.02)}
    .tap-modalTitle{margin:0;font-size:1.42rem;font-weight:800;color:rgba(244,248,255,.97)}.tap-modalSubtitle{margin:8px 0 0;font-size:.84rem;line-height:1.6;color:rgba(214,226,247,.68);max-width:900px}
    .tap-iconBtn{width:40px;height:40px;border-radius:12px;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.04);color:rgba(230,236,247,.92);display:inline-flex;justify-content:center;align-items:center;cursor:pointer;font-size:18px}
    .tap-modalBody{padding:22px 24px 26px}.tap-detail{display:flex;flex-direction:column;gap:20px}.tap-scoreGrid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px}.tap-card{padding:16px}.tap-grid{display:grid;grid-template-columns:1.15fr .85fr;gap:16px}.tap-sectionTitle{margin:0 0 10px}.tap-list{display:flex;flex-direction:column;gap:10px}.tap-listItem{padding:12px 14px}.tap-formGrid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}
    .tap-textarea{width:100%;min-height:130px;border-radius:14px;padding:12px 14px;font-size:.82rem;line-height:1.6;resize:vertical}
    .tap-actionsRow{display:flex;gap:10px;flex-wrap:wrap}.tap-inlineCheck{display:flex;align-items:center;gap:10px;font-size:.78rem;color:rgba(214,226,247,.76)}.tap-inlineCheck input{accent-color:#8b5cf6}
    @media (max-width:1180px){.reservations-container .tap-summary{grid-template-columns:repeat(3,minmax(0,1fr))}.tap-grid{grid-template-columns:1fr}.tap-scoreGrid{grid-template-columns:repeat(2,minmax(0,1fr))}.tap-formGrid{grid-template-columns:1fr 1fr}}
    @media (max-width:860px){.reservations-container.tap-pageShell{padding:8px}.reservations-container .tap-header,.reservations-container .tap-filters,.reservations-container .tap-pagination,.tap-modalHeader{flex-direction:column;align-items:stretch}.reservations-container .tap-summary{grid-template-columns:1fr 1fr}.tap-scoreGrid,.tap-formGrid{grid-template-columns:1fr}.tap-modalBody,.tap-modalHeader{padding:18px}}
    @media (max-width:620px){.reservations-container .tap-summary{grid-template-columns:1fr}}
  `;

  return (
    <div className="reservations-container tap-pageShell">
      <style>{uiCss}</style>
      <section className="tap-shell">
        <div className="tap-header">
          <div>
            <h1 className="tap-title">Trends & Actions</h1>
            <p className="tap-subtitle">
              Turn repeated guest comments into concrete hotel improvements. This view groups the recurring issues, highlights what is growing and keeps the recommended next action, the team decision and the operational constraints together in one place.
            </p>
            {businessUnit?.name ? <div className="tap-hotel">Current hotel: <strong>{businessUnit.name}</strong></div> : null}
          </div>
          <div className="tap-actions">
            <button type="button" className="tap-toolbarBtn" onClick={refreshActionPlanSuggestions} disabled={refreshingPlans}><FiTrendingUp />{refreshingPlans ? "Refreshing..." : "Refresh action plans"}</button>
            <button type="button" className="tap-toolbarBtn" onClick={() => fetchActionPlans({ page: pagination.page || 1, silent: true })} disabled={refreshing || loading}><FiRefreshCw />{refreshing ? "Refreshing..." : "Refresh list"}</button>
          </div>
        </div>
        {feedback ? <div className="tap-notice tap-notice--success">{feedback}</div> : null}
        {error ? <div className="tap-notice tap-notice--error">{error}</div> : null}
        <div className="tap-body">
          <div className="tap-summary">
            <article className="tap-summaryCard"><div className="tap-summaryLabel">Open plans</div><div className="tap-summaryValue">{summary?.open_plans ?? 0}</div><div className="tap-summaryMeta">Topics still waiting for action</div></article>
            <article className="tap-summaryCard"><div className="tap-summaryLabel">In progress</div><div className="tap-summaryValue">{summary?.in_progress_plans ?? 0}</div><div className="tap-summaryMeta">Topics already being worked on</div></article>
            <article className="tap-summaryCard"><div className="tap-summaryLabel">Blocked</div><div className="tap-summaryValue">{summary?.blocked_plans ?? 0}</div><div className="tap-summaryMeta">Topics stopped by constraints</div></article>
            <article className="tap-summaryCard"><div className="tap-summaryLabel">Solved</div><div className="tap-summaryValue">{summary?.solved_plans ?? 0}</div><div className="tap-summaryMeta">Topics with a closed decision</div></article>
            <article className="tap-summaryCard"><div className="tap-summaryLabel">High priority</div><div className="tap-summaryValue">{summary?.high_priority_plans ?? 0}</div><div className="tap-summaryMeta">Need attention sooner</div></article>
            <article className="tap-summaryCard"><div className="tap-summaryLabel">Average urgency</div><div className="tap-summaryValue">{formatScore(summary?.avg_urgency_score)}</div><div className="tap-summaryMeta">Overall pressure across open issues</div></article>
          </div>
          <div className="tap-filters">
            <label className="tap-search"><FiSearch /><input className="tap-input" type="text" placeholder="Search topic, problem, recommendation or constraint" value={filters.search} onChange={handleFilterChange("search")} /></label>
            <select className="tap-select" value={filters.topic_id} onChange={handleFilterChange("topic_id")}><option value="all">All topics</option>{(filterOptions.topics || []).map((topic) => <option key={topic.id} value={String(topic.id)}>{topic.label}</option>)}</select>
            <select className="tap-select" value={filters.status} onChange={handleFilterChange("status")}><option value="all">Any status</option>{(filterOptions.status_options || []).map((status) => <option key={status} value={status}>{getStatusLabel(status)}</option>)}</select>
            <select className="tap-select" value={filters.priority} onChange={handleFilterChange("priority")}><option value="all">Any priority</option>{(filterOptions.priority_options || []).map((priority) => <option key={priority} value={priority}>{getPriorityLabel(priority)}</option>)}</select>
            <button type="button" className="tap-toolbarBtn" onClick={applyFilters}>Apply</button>
          </div>
          <div className="tap-tableWrap">
            {loading ? <div className="tap-empty">Loading action plans...</div> : actionPlans.length ? (
              <table className="tap-table">
                <colgroup><col className="tap-colTopic" /><col /><col className="tap-colScores" /><col className="tap-colPriority" /><col className="tap-colStatus" /><col className="tap-colOwner" /><col /><col className="tap-colOpen" /></colgroup>
                <thead><tr><th>Topic</th><th>What is happening</th><th>Scores</th><th>Priority</th><th>Status</th><th>Owner</th><th>Current recommendation</th><th>Open</th></tr></thead>
                <tbody>{actionPlans.map((plan) => (
                  <tr key={plan.id}>
                    <td><div className="tap-cellTitle">{plan?.topic?.label || "Unknown topic"}</div><div className="tap-cellMeta">{plan?.topic?.topic_group || "General"} topic</div></td>
                    <td><div className="tap-cellText">{truncateText(plan.current_problem_summary, 210) || "No summary yet."}</div>{plan.operational_constraint_summary ? <div className="tap-cellMeta">Constraint memory: {truncateText(plan.operational_constraint_summary, 140)}</div> : null}</td>
                    <td><div className="tap-cellTitle">Urgency {formatScore(plan.urgency_score)}</div><div className="tap-cellMeta">Frequency {formatScore(plan.frequency_score)}</div><div className="tap-cellMeta">Impact {formatScore(plan.impact_score)}</div><div className="tap-cellMeta">Trend {formatScore(plan.trend_score)}</div></td>
                    <td><span className={buildToneClass("tap-chip", getPriorityTone(plan.priority))}>{getPriorityLabel(plan.priority)}</span></td>
                    <td><span className={buildToneClass("tap-chip", getStatusTone(plan.status))}>{getStatusLabel(plan.status)}</span><div className="tap-cellMeta">Reviewed: {formatDate(plan.last_reviewed_at || plan.updated_at)}</div></td>
                    <td><div className="tap-cellTitle">{plan?.owner_user?.name || plan?.owner_user?.email || "Unassigned"}</div></td>
                    <td><div className="tap-cellText">{truncateText(plan.final_recommendation || plan.ai_recommendation, 180) || "Open the plan to write the final recommendation."}</div></td>
                    <td><button type="button" className="rm-topBtn rm-topBtn--violet tap-openBtn" onClick={() => openPlanDetail(plan.id)}><FiEye />Open</button></td>
                  </tr>
                ))}</tbody>
              </table>
            ) : <div className="tap-empty">No action plans yet.<br />Refresh the action plans to build them from recent review patterns.</div>}
          </div>
          <div className="tap-pagination">
            <div className="tap-pageText">{currentRangeText}</div>
            <div className="tap-pageControls">
              <button type="button" className="tap-pageBtn" onClick={() => handlePageChange((pagination.page || 1) - 1)} disabled={(pagination.page || 1) <= 1 || refreshing}>Previous</button>
              <span className="tap-pageText">Page {pagination.page || 1} of {pagination.total_pages || 1}</span>
              <button type="button" className="tap-pageBtn" onClick={() => handlePageChange((pagination.page || 1) + 1)} disabled={(pagination.page || 1) >= (pagination.total_pages || 1) || refreshing}>Next</button>
            </div>
          </div>
        </div>
      </section>
      <Modal open={Boolean(selectedPlanId)} onClose={closePlanDetail} title={selectedPlanDetail?.action_plan?.topic?.label || "Action plan detail"} subtitle="Review why this topic matters, what guests are saying, what the system suggests and what decision the hotel wants to keep as the final operational answer.">
        {detailLoading ? <div className="tap-empty">Loading action plan detail...</div> : null}
        {!detailLoading && detailError ? <div className="tap-notice tap-notice--error" style={{ margin: 0 }}>{detailError}</div> : null}
        {!detailLoading && !detailError && selectedPlanDetail?.action_plan ? (
          <div className="tap-detail">
            <div className="tap-scoreGrid">
              <article className="tap-card"><div className="tap-cardLabel">Frequency</div><div className="tap-cardValue">{formatScore(selectedPlanDetail.action_plan.frequency_score)}</div><div className="tap-cardMeta">How often the issue appears in recent reviews</div></article>
              <article className="tap-card"><div className="tap-cardLabel">Impact</div><div className="tap-cardValue">{formatScore(selectedPlanDetail.action_plan.impact_score)}</div><div className="tap-cardMeta">How damaging the issue looks when it appears</div></article>
              <article className="tap-card"><div className="tap-cardLabel">Trend</div><div className="tap-cardValue">{formatScore(selectedPlanDetail.action_plan.trend_score)}</div><div className="tap-cardMeta">Whether mentions are rising compared with the prior period</div></article>
              <article className="tap-card"><div className="tap-cardLabel">Urgency</div><div className="tap-cardValue">{formatScore(selectedPlanDetail.action_plan.urgency_score)}</div><div className="tap-cardMeta">Overall need to act soon on this topic</div></article>
            </div>
            <div className="tap-grid">
              <article className="tap-card"><h3 className="tap-sectionTitle">What is happening</h3><p className="tap-sectionText">{selectedPlanDetail.action_plan.current_problem_summary || "No problem summary available yet."}</p>{selectedPlanDetail.action_plan.human_feedback ? <p className="tap-sectionText"><strong>Latest team note:</strong>{"\n"}{selectedPlanDetail.action_plan.human_feedback}</p> : null}</article>
              <article className="tap-card"><h3 className="tap-sectionTitle">Recommended next move</h3><p className="tap-sectionText">{selectedPlanDetail.action_plan.final_recommendation || selectedPlanDetail.action_plan.ai_recommendation || "No recommendation available yet."}</p>{selectedPlanDetail.action_plan.operational_constraint_summary ? <p className="tap-sectionText"><strong>Constraint memory:</strong>{"\n"}{selectedPlanDetail.action_plan.operational_constraint_summary}</p> : null}</article>
            </div>
            <div className="tap-grid">
              <article className="tap-card">
                <h3 className="tap-sectionTitle">Example reviews</h3>
                {selectedPlanDetail.example_reviews?.length ? <div className="tap-list">{selectedPlanDetail.example_reviews.map((entry) => <div key={`${entry.review_id}-${entry.evidence_text || "review"}`} className="tap-listItem"><div className="tap-cellTitle">{entry.review?.guest_display_name || entry.review?.guest_name || "Anonymous guest"} · {entry.review?.source?.name || "Unknown channel"}</div><div className="tap-cellMeta">{formatDate(entry.review?.review_date || entry.review?.created_at)} · Score {entry.review?.rating_value}/{entry.review?.rating_scale_max || 10} · Criticality {formatScore(entry.review?.critical_points_score)}</div><div className="tap-cellText">{truncateText(entry.evidence_text || entry.review?.negative_text || entry.review?.review_text || entry.review?.title, 250)}</div>{entry.latest_ai_analysis?.suggested_internal_action ? <div className="tap-cellMeta">Suggested action: {truncateText(entry.latest_ai_analysis.suggested_internal_action, 180)}</div> : null}</div>)}</div> : <p className="tap-sectionText">No example reviews were found for this topic yet.</p>}
              </article>
              <article className="tap-card">
                <h3 className="tap-sectionTitle">Operational constraints</h3>
                {selectedPlanDetail.constraints?.length ? <div className="tap-list">{selectedPlanDetail.constraints.map((constraint) => <div key={constraint.id} className="tap-listItem"><div className="tap-cellTitle">{constraint.constraint_summary}</div><div className="tap-cellMeta">{constraint.applies_globally ? "Applies to the whole hotel" : constraint.topic?.label || "Topic-specific"} · Saved {formatDate(constraint.created_at)}</div>{constraint.constraint_details ? <div className="tap-cellText">{truncateText(constraint.constraint_details, 220)}</div> : null}</div>)}</div> : <p className="tap-sectionText">No operational constraints have been saved for this topic yet.</p>}
              </article>
            </div>
            <div className="tap-grid">
              <article className="tap-card">
                <h3 className="tap-sectionTitle">Update the action plan</h3>
                <div className="tap-formGrid">
                  <label><div className="tap-help">Status</div><select className="tap-select" value={planForm.status} onChange={(event) => setPlanForm((current) => ({ ...current, status: event.target.value }))}>{(filterOptions.status_options || []).map((status) => <option key={status} value={status}>{getStatusLabel(status)}</option>)}</select></label>
                  <label><div className="tap-help">Priority</div><select className="tap-select" value={planForm.priority} onChange={(event) => setPlanForm((current) => ({ ...current, priority: event.target.value }))}>{(filterOptions.priority_options || []).map((priority) => <option key={priority} value={priority}>{getPriorityLabel(priority)}</option>)}</select></label>
                  <label><div className="tap-help">Owner</div><select className="tap-select" value={planForm.owner_user_id} onChange={(event) => setPlanForm((current) => ({ ...current, owner_user_id: event.target.value }))}><option value="">Unassigned</option>{(selectedPlanDetail.owner_options || []).map((owner) => <option key={owner.id} value={String(owner.id)}>{owner.name}</option>)}</select></label>
                </div>
                <div style={{ marginTop: 12 }}><div className="tap-help">Final recommendation for the hotel</div><textarea className="tap-textarea" value={planForm.final_recommendation} onChange={(event) => setPlanForm((current) => ({ ...current, final_recommendation: event.target.value }))} placeholder="Write the final recommendation that the hotel will follow for this issue." /></div>
                <div style={{ marginTop: 12 }}><div className="tap-help">Internal note</div><textarea className="tap-textarea" style={{ minHeight: 110 }} value={planForm.human_feedback} onChange={(event) => setPlanForm((current) => ({ ...current, human_feedback: event.target.value }))} placeholder="Add the latest operational note, clarification or decision taken by the team." /></div>
                <div className="tap-actionsRow" style={{ marginTop: 12 }}><button type="button" className="tap-toolbarBtn" onClick={saveActionPlan} disabled={Boolean(actionLoading)}><FiEdit3 />{actionLoading === "save_plan" ? "Saving..." : "Save action plan"}</button></div>
              </article>
              <article className="tap-card">
                <h3 className="tap-sectionTitle">Add team feedback</h3>
                <div className="tap-formGrid">
                  <label><div className="tap-help">Feedback type</div><select className="tap-select" value={feedbackForm.feedback_type} onChange={(event) => setFeedbackForm((current) => ({ ...current, feedback_type: event.target.value }))}><option value="clarification">Clarification</option><option value="accepted">Accepted</option><option value="rejected">Rejected</option><option value="constraint">Constraint</option><option value="alternative_proposal">Alternative proposal</option></select></label>
                  <label style={{ gridColumn: "span 2" }}><div className="tap-help">Short label for a saved constraint</div><input className="tap-input" type="text" value={feedbackForm.constraint_summary} onChange={(event) => setFeedbackForm((current) => ({ ...current, constraint_summary: event.target.value }))} placeholder="Optional short summary, for example Building is protected" style={{ paddingLeft: 12 }} /></label>
                </div>
                <div style={{ marginTop: 12 }}><div className="tap-help">If you mark a constraint here, the system will remember it and future AI recommendations should avoid proposing the same unworkable fix again.</div><textarea className="tap-textarea" value={feedbackForm.feedback_text} onChange={(event) => setFeedbackForm((current) => ({ ...current, feedback_text: event.target.value }))} placeholder="Write what the hotel has decided, why the proposal is not viable, or what alternative should be preferred." /></div>
                <label className="tap-inlineCheck" style={{ marginTop: 12 }}><input type="checkbox" checked={feedbackForm.is_global_constraint} onChange={(event) => setFeedbackForm((current) => ({ ...current, is_global_constraint: event.target.checked }))} />This is a global hotel constraint, not only for this topic</label>
                <div className="tap-actionsRow" style={{ marginTop: 12 }}><button type="button" className="tap-toolbarBtn" onClick={saveFeedbackEntry} disabled={Boolean(actionLoading)}><FiMessageSquare />{actionLoading === "save_feedback" ? "Saving..." : "Save feedback"}</button></div>
              </article>
            </div>
            <article className="tap-card">
              <h3 className="tap-sectionTitle">Feedback history</h3>
              {selectedPlanDetail.feedback_entries?.length ? <div className="tap-list">{selectedPlanDetail.feedback_entries.map((entry) => <div key={entry.id} className="tap-listItem"><div className="tap-cellTitle">{entry.user?.name || entry.user?.email || "Team member"} · {entry.feedback_type.replace(/_/g, " ")}</div><div className="tap-cellMeta">{entry.is_global_constraint ? "Global constraint" : "Topic feedback"} · {formatDate(entry.created_at)}</div><div className="tap-cellText">{entry.feedback_text}</div></div>)}</div> : <p className="tap-sectionText">No feedback has been saved for this plan yet.</p>}
            </article>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

export default TrendsActionsPage;
